/* CASH REPORT — TEMPLATE 1 AS TUAN DREW IT (2026-10-09).

   Max, reading the Import screen beside "03. Weekly cash Report - format (1).xlsx": Field 1 was
   missing, Field 2 did not have his columns, Fields 3 and 4 were shaped differently and Fields 5 and
   6 sat side by side with a memo too narrow to write in. The form (CashForms) and the read-only view
   (CashBodies) both draw Template 1 from the pieces here, so the two cannot come to lay the same
   version out two ways — and both follow his sheet, field by field.

   BALANCES ONLY since 2026-10-29 (Tuan: "Weekly Cash Report sẽ chỉ còn cần cái Closing Balance thôi",
   Fields 3 and 4 "cũng k cần luôn", the intercompany row "cũng k cần hiện thị nữa"):

     Field 1  the entity in SGD: Closing · Restricted · Fixed Deposit · Available
     Field 2  every account: Closing (per the bank) · Restricted · Fixed Deposit · Available ·
              Exchange Rate · Restricted Account · Fixed Deposit · Upload Statement · Upload Balance screenshot
     Field 5  Top 3 key spendings this week, then Field 6, Top 3 payable next week — one above the
              other, the memo as wide as the page. Their numbers stay as Tuan's sheet has them. */
import React from 'react';
import { FileText, Paperclip } from 'lucide-react';
import { Num } from './cashUi';

export const r2 = (v) => Math.round(v * 100) / 100;

/* Field 1 — the four balances the server's computeReport totals, in SGD. `totals` is null
   until every account has an exchange rate: a sum over the accounts that happen to have one is a
   row of figures that are each wrong, and the Data Check already says which rate is missing. */
export function Field1({ t, totals }) {
  const cols = ['closing', 'restricted', 'fixed_deposit', 'available'];
  return (
    <>
      <h4 className="cw-h">{t('cashf.field1')}</h4>
      <div className="cs-table-wrap">
        <table className="pa-table cw-table t1-f1" id="t1-field1">
          <thead><tr>
            <th colSpan={2} />
            {cols.map((k) => <th key={k} className={`num t1-h-${k}`}>{t(`cashw.tot_${k}`)}</th>)}
          </tr></thead>
          <tbody>
            <tr data-row="overall">
              <td className="t1-f1-ent">{t('cashf.f1Entity')}</td><td className="t1-f1-lbl">{t('cashf.f1Overall')}</td>
              {cols.map((k) => <td key={k} className="num"><b>{totals ? <Num v={totals[k]} ccy="SGD" /> : '—'}</b></td>)}
            </tr>
          </tbody>
        </table>
      </div>
      {!totals && <p className="cs-hint t1-norate">{t('cashf.f1NoRate')}</p>}
    </>
  );
}

/* Field 2's head, written once for the form and the view. */
export function Field2Head({ t }) {
  return (
    <thead><tr>
      <th>{t('cashw.h_account')}</th><th>{t('cashw.h_ccy')}</th>
      <th className="num">{t('cashw.tot_closing')}</th>
      <th className="num t1-h-restricted">{t('cashw.tot_restricted')}</th><th className="num t1-h-fixed_deposit">{t('cashw.tot_fixed_deposit')}</th>
      <th className="num t1-h-available">{t('cashw.tot_available')}</th><th className="num">{t('cashf.h_rate')}</th>
      <th className="cf-center">{t('cashf.h_restrictedAcct')}</th>
      <th className="cf-center">{t('cashf.h_fdAcct')}</th><th>{t('cashf.h_upStatement')}</th><th>{t('cashf.h_upBalance')}</th>
    </tr></thead>
  );
}

/* Restricted / Fixed Deposit are the ACCOUNT's type in Set up, shown where Tuan's sheet ticks
   them — read-only, so one account cannot be restricted one week and free the next. */
export function SetupTick({ t, on, label }) {
  return <input type="checkbox" checked={!!on} disabled readOnly aria-label={label} title={t('cashf.fromSetup')} />;
}

/* An "Upload Statement" / "Upload Balance screenshot" cell: what is attached for that account and
   kind, and — where the reader may attach — the button. */
export function FileCell({ t, kind, account, filesFor, fileLink, onAttach, disabled }) {
  const have = (filesFor ? filesFor(account) : []).filter((f) => f.kind === kind);
  return (
    <td className="cf-files">
      {have.map((f) => (
        <a key={f.file_pk} className="cw-file" href={fileLink(f)} target="_blank" rel="noreferrer" title={f.original_name}>
          <FileText size={12} /> {f.original_name.length > 16 ? `${f.original_name.slice(0, 14)}…` : f.original_name}
        </a>
      ))}
      {onAttach && (
        <label className={`cf-attach${disabled ? ' disabled' : ''}`} title={t(`cashw.kind_${kind}`)}>
          <Paperclip size={12} /> {t(kind === 'statement' ? 'cashf.upStatement' : 'cashf.upBalance')}
          <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" hidden disabled={disabled}
            onChange={(e) => { onAttach(e.target.files[0], kind, account); e.target.value = ''; }} />
        </label>
      )}
    </td>
  );
}

/* Field 5 or Field 6 — its own heading and its own table, one above the other (Max: "để trên dưới
   vì cái memo có thể điền dài"). `edit` gives the boxes; without it the same table reads. */
export function TopTable({ t, kind, rows, edit, categories = [], onChange, catName }) {
  return (
    <>
      <h4 className="cw-h">{t(kind === 'spend' ? 'cashf.field5' : 'cashf.field6')}</h4>
      <table className="pa-table cw-table cf-table t1-top" data-kind={kind}>
        <colgroup><col style={{ width: 52 }} /><col style={{ width: 240 }} /><col /><col style={{ width: 170 }} /></colgroup>
        <thead><tr>
          <th className="cf-center">{t('cashf.h_no')}</th><th>{t('cashf.h_category')}</th>
          <th>{t('cashf.h_memo')}</th><th className="num">{t('cashf.h_amountSgd')}</th>
        </tr></thead>
        <tbody>
          {rows.map((x) => (
            <tr key={x.rank_no} data-rank={x.rank_no}>
              <td className="cf-center">{x.rank_no}</td>
              {edit ? (
                <>
                  <td><select value={x.category_code} aria-label={t(kind === 'spend' ? 'xCash.aria.spendCategory' : 'xCash.aria.payableCategory', { n: x.rank_no })} onChange={(e) => onChange(x.rank_no, 'category_code', e.target.value)}>
                    <option value="">{t('cash.choose')}</option>
                    {/* "Code - Name", as the import file's dropdown reads (Max, 2026-10-13). */}
                    {categories.map((c) => <option key={c.code} value={c.code}>{[c.code, c.name].filter(Boolean).join(' - ')}</option>)}
                  </select></td>
                  <td><textarea className="t1-memo" rows={2} maxLength={1000} value={x.memo} aria-label={t(kind === 'spend' ? 'xCash.aria.spendMemo' : 'xCash.aria.payableMemo', { n: x.rank_no })}
                    placeholder={t('cashf.memoPh')} onChange={(e) => onChange(x.rank_no, 'memo', e.target.value)} /></td>
                  <td>{edit.amountBox(x)}</td>
                </>
              ) : (
                <>
                  <td>{x.category_code ? [x.category_code, catName?.(x)].filter(Boolean).join(' - ') : ''}</td>
                  <td className="cs-wrap t1-memo-read">{x.memo || ''}</td>
                  <td className="num"><Num v={x.amount_sgd == null ? null : Number(x.amount_sgd)} ccy="SGD" /></td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
